import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  TrendingUp, TrendingDown, Minus, Settings, LogOut, RefreshCw, Send, 
  AlertTriangle, Shield, ChevronUp, ChevronDown, Crown, Sparkles, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { InfoTooltip } from "../components/ui/info-tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Infobulles contextuelles
const TOOLTIPS = {
  // Régime économique
  regime: "Le régime économique reflète la phase actuelle du cycle : croissance forte ou faible, inflation haute ou basse. Il détermine quels secteurs sont historiquement favorisés.",
  confidence: "Indique à quel point les indicateurs économiques convergent vers ce régime. Plus c'est élevé, plus le signal est clair. Sous 60%, les signaux sont mitigés.",
  transitionAlert: "Signal avancé d'un possible changement de régime dans les mois à venir. Ce n'est pas une certitude, mais un indicateur à surveiller.",
  
  // Indicateurs macro
  gdp: "Croissance économique sur 12 mois. Au-dessus de 2% = croissance solide. En dessous de 1% = ralentissement.",
  cpi: "Hausse des prix sur 12 mois. En dessous de 3% = maîtrisée. Au-dessus de 4% = élevée, pèse sur le pouvoir d'achat.",
  yieldCurve: "Écart entre les taux longs (10 ans) et courts (2 ans). Positif = normal. Négatif (inversé) = signal historique de récession à venir dans 12-18 mois.",
  
  // Secteurs
  sectorScore: "Score combinant la performance récente du secteur par rapport au marché et son alignement avec le régime économique actuel. Plus c'est élevé, plus le secteur est favorisé.",
  signalSurponderer: "Ce secteur surperforme et bénéficie du régime actuel. Historiquement favorable dans cette phase du cycle.",
  signalNeutre: "Ce secteur n'a pas de tendance marquée actuellement. Ni particulièrement favorisé, ni défavorisé.",
  signalSousponderer: "Ce secteur sous-performe ou est mal aligné avec le régime actuel. Historiquement défavorable dans cette phase du cycle.",
  relativePerf: "Performance du secteur sur 3 mois comparée au marché global. Positif = surperforme le marché. Négatif = sous-performe.",
  incomeCapShield: "Protection concentration active. Votre exposition à ce secteur est limitée car c'est aussi votre secteur d'emploi. Cela réduit le risque de double perte en cas de crise sectorielle.",
  
  // Briefing & QA
  briefing: "Analyse personnalisée générée par intelligence artificielle, basée sur les données macro actuelles et votre profil. Mis à jour chaque semaine.",
  briefingRefresh: "Régénère le briefing avec les données les plus récentes. Consomme 1 briefing sur votre quota gratuit.",
  qaZone: "Posez n'importe quelle question sur les secteurs, le régime économique ou vos recommandations. L'IA répond en s'appuyant uniquement sur les données actuelles.",
  
  // Limites
  freeBriefings: "En version gratuite, vous pouvez consulter 3 briefings au total. Passez à Premium pour un accès illimité.",
  freeQuestions: "En version gratuite, vous pouvez poser 10 questions par mois à l'assistant. Premium = illimité.",
};

// Helper pour obtenir l'infobulle d'un indicateur
const getIndicatorTooltip = (name) => {
  if (name.toLowerCase().includes("pib") || name.toLowerCase().includes("gdp") || name.toLowerCase().includes("croissance")) {
    return TOOLTIPS.gdp;
  }
  if (name.toLowerCase().includes("inflation") || name.toLowerCase().includes("cpi")) {
    return TOOLTIPS.cpi;
  }
  if (name.toLowerCase().includes("courbe") || name.toLowerCase().includes("taux") || name.toLowerCase().includes("yield")) {
    return TOOLTIPS.yieldCurve;
  }
  return null;
};

// Helper pour obtenir l'infobulle d'un signal
const getSignalTooltip = (signal) => {
  switch(signal) {
    case "surponderer": return TOOLTIPS.signalSurponderer;
    case "neutre": return TOOLTIPS.signalNeutre;
    case "sous-ponderer": return TOOLTIPS.signalSousponderer;
    default: return null;
  }
};

const REGIME_LABELS = {
  expansion: "Expansion",
  reprise: "Reprise",
  stagflation: "Stagflation",
  ralentissement: "Ralentissement",
  recession: "Risque de récession",
  mixte: "Mixte"
};

const REGIME_COLORS = {
  expansion: "bg-emerald-500",
  reprise: "bg-blue-500",
  stagflation: "bg-orange-500",
  ralentissement: "bg-amber-500",
  recession: "bg-red-500",
  mixte: "bg-gray-500"
};

const SIGNAL_LABELS = {
  surponderer: "Surpondérer",
  neutre: "Neutre",
  "sous-ponderer": "Sous-pondérer"
};

const SIGNAL_COLORS = {
  surponderer: "text-emerald-400",
  neutre: "text-gray-400",
  "sous-ponderer": "text-red-400"
};

const REGIONS = [
  { code: "us", name: "États-Unis", flag: "🇺🇸" },
  { code: "eu", name: "Zone Euro", flag: "🇪🇺" },
  { code: "uk", name: "Royaume-Uni", flag: "🇬🇧" },
  { code: "jp", name: "Japon", flag: "🇯🇵" },
];

export default function DashboardPage({ user, setUser, hideNav = false }) {
  const navigate = useNavigate();
  const [marketData, setMarketData] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [qaHistory, setQaHistory] = useState([]);
  const [question, setQuestion] = useState("");
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [qaLoading, setQaLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("us");

  useEffect(() => {
    fetchData(selectedRegion);
  }, [selectedRegion]);

  const fetchData = async (region = "us") => {
    setLoading(true);
    try {
      const [marketRes, briefingRes, qaRes, limitsRes] = await Promise.all([
        axios.get(`${API}/market/data?region=${region}`),
        axios.get(`${API}/briefing/latest`),
        axios.get(`${API}/qa/history`),
        axios.get(`${API}/user/limits`)
      ]);
      
      setMarketData(marketRes.data);
      setBriefing(briefingRes.data);
      setQaHistory(qaRes.data || []);
      setLimits(limitsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const generateBriefing = async () => {
    setBriefingLoading(true);
    try {
      const response = await axios.post(`${API}/briefing/generate`, { force_refresh: true });
      setBriefing(response.data);
      
      // Update limits
      const limitsRes = await axios.get(`${API}/user/limits`);
      setLimits(limitsRes.data);
      
      toast.success("Briefing généré avec succès");
    } catch (error) {
      if (error.response?.status === 402) {
        setPaywallReason("briefings");
        setShowPaywall(true);
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors de la génération du briefing");
      }
    } finally {
      setBriefingLoading(false);
    }
  };

  const askQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    
    setQaLoading(true);
    try {
      const response = await axios.post(`${API}/qa/ask`, { question: question.trim() });
      setQaHistory([{ question: question.trim(), answer: response.data.answer, created_at: response.data.created_at }, ...qaHistory]);
      setQuestion("");
      
      // Update limits
      const limitsRes = await axios.get(`${API}/user/limits`);
      setLimits(limitsRes.data);
    } catch (error) {
      if (error.response?.status === 402) {
        setPaywallReason("questions");
        setShowPaywall(true);
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors du traitement de la question");
      }
    } finally {
      setQaLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleUpgrade = async () => {
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Erreur lors de la création de la session de paiement");
    }
  };

  const TrendIcon = ({ trend }) => {
    if (trend === "up") return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (trend === "down") return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={hideNav ? "" : "min-h-screen bg-background"}>
      {/* Header - only show if not using unified navbar */}
      {!hideNav && (
        <header className="glass sticky top-0 z-50 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold tracking-tight font-manrope hidden sm:inline">Oracle Portfolio Advisor</span>
              <span className="font-bold tracking-tight font-manrope sm:hidden">Oracle</span>
            </div>
            
            <div className="flex items-center gap-2">
              {user?.is_premium && (
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                  <Crown className="w-3 h-3 mr-1" />
                  Premium
                </Badge>
              )}
              <Button data-testid="settings-btn" variant="ghost" size="icon" onClick={() => navigate("/settings")}>
                <Settings className="w-5 h-5" />
              </Button>
              <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>
      )}

      {/* Main content */}
      <div className={hideNav ? "" : "max-w-7xl mx-auto px-4 py-6"}>
        {/* Stale data warning */}
        {marketData?.is_stale && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-sm text-amber-200">{marketData.stale_reason}</p>
          </div>
        )}

        {/* Region Selector */}
        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <span className="text-sm text-muted-foreground">Région :</span>
          <div className="flex gap-2">
            {REGIONS.map((region) => (
              <Button
                key={region.code}
                variant={selectedRegion === region.code ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedRegion(region.code)}
                className={`gap-2 ${selectedRegion === region.code ? 'bg-primary' : ''}`}
                data-testid={`region-${region.code}`}
              >
                <span className="text-lg">{region.flag}</span>
                <span className="hidden sm:inline">{region.name}</span>
              </Button>
            ))}
          </div>
          {marketData?.region && (
            <Badge variant="outline" className="ml-auto">
              Source: {marketData.region.source}
            </Badge>
          )}
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-4">
          
          {/* Regime Card - Spans 4 columns */}
          <Card data-testid="regime-card" className="md:col-span-4 lg:col-span-4 bg-card border-border card-hover">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Régime économique</CardTitle>
                  <InfoTooltip content={TOOLTIPS.regime} />
                </div>
                <span className="text-2xl">{marketData?.region?.flag || "🇺🇸"}</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3 h-3 rounded-full ${REGIME_COLORS[marketData?.regime?.name] || 'bg-gray-500'}`}></div>
                <span className="text-2xl font-bold font-manrope">{REGIME_LABELS[marketData?.regime?.name] || marketData?.regime?.name}</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">Confiance</span>
                    <InfoTooltip content={TOOLTIPS.confidence} />
                  </div>
                  <span className="font-mono font-semibold">{marketData?.regime?.confidence}%</span>
                </div>
                <Progress value={marketData?.regime?.confidence} className="h-2" />
              </div>
              
              {marketData?.regime?.transition_alert && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm pulse-alert">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-start gap-1.5">
                        <p className="text-sm text-amber-200">{marketData.regime.transition_alert}</p>
                        <InfoTooltip content={TOOLTIPS.transitionAlert} iconClassName="text-amber-400" />
                      </div>
                      {marketData.regime.alert_probability && (
                        <p className="text-xs text-amber-400 mt-1">Probabilité : {marketData.regime.alert_probability}%</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Macro Indicators - 3 cards spanning 8 columns total */}
          {marketData?.indicators?.map((indicator, index) => (
            <Card key={indicator.name} data-testid={`indicator-${index}`} className="md:col-span-2 lg:col-span-2 bg-card border-border card-hover animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{indicator.name}</span>
                    {getIndicatorTooltip(indicator.name) && (
                      <InfoTooltip content={getIndicatorTooltip(indicator.name)} />
                    )}
                  </div>
                  <TrendIcon trend={indicator.trend} />
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-bold font-mono ${
                    indicator.name.includes("Courbe") 
                      ? (indicator.value < 0 ? "text-red-400" : "text-emerald-400")
                      : ""
                  }`}>
                    {indicator.value > 0 && indicator.name.includes("Courbe") ? "+" : ""}{indicator.value}
                  </span>
                  <span className="text-muted-foreground">{indicator.unit}</span>
                </div>
                {indicator.previous_value !== null && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Précédent : {indicator.previous_value}{indicator.unit}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
          
          {/* Empty spacer for alignment */}
          <div className="hidden lg:block lg:col-span-2"></div>

          {/* Sectors List - Spans 6 columns */}
          <Card data-testid="sectors-card" className="md:col-span-4 lg:col-span-6 bg-card border-border">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Secteurs</CardTitle>
                <InfoTooltip content={TOOLTIPS.sectorScore} />
              </div>
              <div className="flex gap-2 text-xs items-center">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-red-400 cursor-help">Sous-pondérer</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px] bg-popover text-popover-foreground border border-border p-3">
                      {TOOLTIPS.signalSousponderer}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="text-muted-foreground">|</span>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-emerald-400 cursor-help">Surpondérer</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px] bg-popover text-popover-foreground border border-border p-3">
                      {TOOLTIPS.signalSurponderer}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {marketData?.sectors?.map((sector, index) => (
                  <div 
                    key={sector.sector} 
                    data-testid={`sector-${index}`}
                    className="flex items-center gap-4 p-3 bg-background/50 rounded-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{sector.sector}</span>
                        <span className="text-xs text-muted-foreground font-mono bg-muted/50 px-1.5 py-0.5 rounded">
                          {sector.ticker}
                        </span>
                        {sector.is_capped && (
                          <InfoTooltip 
                            content={TOOLTIPS.incomeCapShield}
                            iconClassName="text-primary"
                          >
                            <span className="cursor-help">
                              <Shield className="w-4 h-4 text-primary" />
                            </span>
                          </InfoTooltip>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`text-xs cursor-help ${SIGNAL_COLORS[sector.signal]}`}>
                                {SIGNAL_LABELS[sector.signal]}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[280px] bg-popover text-popover-foreground border border-border p-3">
                              {getSignalTooltip(sector.signal)}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <span className="text-xs text-muted-foreground">•</span>
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`text-xs font-mono cursor-help ${sector.relative_performance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {sector.relative_performance >= 0 ? '+' : ''}{sector.relative_performance}%
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[280px] bg-popover text-popover-foreground border border-border p-3">
                              {TOOLTIPS.relativePerf}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    <div className="w-24 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-background rounded-full overflow-hidden">
                        <div 
                          className="h-full score-bar"
                          style={{ width: `${sector.score}%` }}
                        ></div>
                      </div>
                      <span className="font-mono font-semibold text-sm w-8">{sector.score}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Briefing Section - Spans 6 columns */}
          <Card data-testid="briefing-card" className="md:col-span-4 lg:col-span-6 bg-card border-border">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Briefing hebdomadaire</CardTitle>
                  <InfoTooltip content={TOOLTIPS.briefing} />
                </div>
                {briefing && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Généré le {new Date(briefing.created_at).toLocaleDateString('fr-FR', { 
                      day: 'numeric', month: 'long', year: 'numeric' 
                    })}
                  </p>
                )}
              </div>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      data-testid="generate-briefing-btn"
                      variant="outline" 
                      size="sm" 
                      onClick={generateBriefing}
                      disabled={briefingLoading}
                      className="gap-2"
                    >
                      {briefingLoading ? (
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      {briefing ? "Régénérer" : "Générer"}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[280px] bg-popover text-popover-foreground border border-border p-3">
                    {TOOLTIPS.briefingRefresh}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardHeader>
            <CardContent>
              {!limits?.is_premium && limits?.briefings_limit && (
                <div className="mb-4 p-2 bg-muted rounded-sm text-xs text-muted-foreground flex items-center gap-2">
                  <span>Briefings utilisés : {limits.briefings_used}/{limits.briefings_limit}</span>
                  <InfoTooltip content={TOOLTIPS.freeBriefings} />
                </div>
              )}
              
              {briefing ? (
                <ScrollArea className="h-[300px] pr-4">
                  <div className="prose prose-sm prose-invert max-w-none">
                    {briefing.content.split('\n').map((paragraph, i) => (
                      <p key={i} className="text-sm leading-relaxed mb-3">{paragraph}</p>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-center">
                  <div>
                    <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">Aucun briefing généré</p>
                    <p className="text-xs text-muted-foreground">Cliquez sur "Générer" pour obtenir votre briefing personnalisé</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Q&A Section - Full width */}
          <Card data-testid="qa-card" className="md:col-span-4 lg:col-span-12 bg-card border-border">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Questions à Oracle</CardTitle>
                <InfoTooltip content={TOOLTIPS.qaZone} />
              </div>
              {!limits?.is_premium && limits?.qa_limit && (
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <span>Questions ce mois : {limits.qa_used_this_month}/{limits.qa_limit}</span>
                  <InfoTooltip content={TOOLTIPS.freeQuestions} />
                </p>
              )}
            </CardHeader>
            <CardContent>
              {/* Quick questions */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs text-muted-foreground mr-2">Questions suggérées :</span>
                {[
                  "Pourquoi la Tech est-elle favorisée ?",
                  "Quels risques pour les 6 prochains mois ?",
                  "Comment interpréter la courbe des taux ?"
                ].map((q) => (
                  <Button
                    key={q}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => setQuestion(q)}
                  >
                    {q}
                  </Button>
                ))}
              </div>
              
              {/* Question input */}
              <form onSubmit={askQuestion} className="flex gap-2 mb-4">
                <input
                  data-testid="qa-input"
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Posez une question à Oracle..."
                  className="flex-1 px-4 py-2 bg-background border border-border rounded-sm text-sm focus:outline-none focus:border-primary"
                />
                <Button 
                  data-testid="qa-submit-btn"
                  type="submit" 
                  disabled={!question.trim() || qaLoading}
                  className="gap-2 rounded-sm"
                >
                  {qaLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Envoyer
                </Button>
              </form>
              
              {/* Q&A History */}
              <ScrollArea className="h-[250px]">
                <div className="space-y-4 pr-4">
                  {qaHistory.length > 0 ? (
                    qaHistory.map((qa, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <span className="text-xs font-medium">Q</span>
                          </div>
                          <p className="text-sm">{qa.question}</p>
                        </div>
                        <div className="flex items-start gap-2 ml-8">
                          <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                            <TrendingUp className="w-3 h-3 text-emerald-400" />
                          </div>
                          <p className="text-sm text-muted-foreground">{qa.answer}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground text-sm">Aucune question posée</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Paywall Modal */}
      <Dialog open={showPaywall} onOpenChange={setShowPaywall}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-manrope">
              <Crown className="w-6 h-6 text-amber-500" />
              Passez à Oracle Premium
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {paywallReason === "briefings" 
                ? "Vous avez utilisé vos 3 briefings gratuits."
                : "Vous avez atteint la limite de 10 questions ce mois-ci."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center">
              <div className="text-4xl font-bold font-manrope mb-1">199€<span className="text-lg text-muted-foreground">/an</span></div>
              <p className="text-sm text-muted-foreground">Soit moins de 17€/mois</p>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm">Briefings illimités</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Send className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm">Questions Q&A illimitées</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm">Accès aux futures fonctionnalités</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button 
              data-testid="upgrade-btn"
              onClick={handleUpgrade}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-sm"
            >
              <Crown className="w-4 h-4 mr-2" />
              Passer à Premium
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setShowPaywall(false)}
              className="w-full"
            >
              Continuer en gratuit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
